// /anniversaries/new — create page (W3.1 §5.3).
//
// PageHero + sections + PageBottomActionBar (sticky).

import { useState } from "react";
import { useJsonDraft } from "@/lib/hooks/useDraft";
import { useNavigate } from "react-router-dom";


import { PageHero } from "@/components/primitives/PageHero";
import { PageBottomActionBar } from "@/components/system/PageBottomActionBar";
import { useHidePillNav } from "@/components/system/BottomChrome";
import { FormErrorBanner } from "@/components/auth/FormField";
import { PageContainer, SectionStack } from "@/components/layout";
import { AISuggestChip } from "./components/AISuggestChip";
import { CategoryPicker } from "./components/CategoryPicker";
import { ReminderDaysPicker } from "./components/ReminderDaysPicker";
import { useCreateAnniversary } from "@/lib/hooks/anniversary";
import {
  anniversaryCreateSchema,
  type AnniversaryCreateParsed,
} from "@/lib/validation/anniversary";
import { ApiError } from "@/lib/api/pair";
import { useToast } from "@/components/ui/toast";

export function NewAnniversaryPage() {
  const nav = useNavigate();
  const createMut = useCreateAnniversary();
  const { push: pushToast } = useToast();
  // W35.6: this page docks the submit action, so it owns the bottom edge and
  // the PillNav steps aside (see BottomChrome).
  useHidePillNav();
  // W39.76 — measured: the probe pointed at `#ann-description` on this page and
  // reported `drafts parked: []` and an empty field after a revoked session. The
  // draft is the **whole form**, not just the long field — bringing the
  // description back and leaving the title gone would look like a half-saved form.
  const formDraft = useJsonDraft("anniversary-new", {
    title: "",
    description: "",
    customLabel: "",
  });
  const [title, setTitle] = useState(formDraft.value.title);
  const [description, setDescription] = useState(formDraft.value.description);
  const today = new Date();
  const defaultDate = today.toISOString().slice(0, 10);
  const [date, setDate] = useState(defaultDate);
  const [category, setCategory] = useState("anniversary");
  const [customLabel, setCustomLabel] = useState(formDraft.value.customLabel);
  const [isRecurring, setIsRecurring] = useState(true);
  const [reminderDays, setReminderDays] = useState<number[]>([]);
  const [reminderTime, setReminderTime] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const submit = async () => {
    setFormError(null);
    const parsed = anniversaryCreateSchema.safeParse({
      title: title.trim(),
      description: description.trim() || null,
      date,
      category,
      custom_category_label: category === "custom" ? customLabel.trim() || null : null,
      is_recurring: isRecurring,
      reminder_days: reminderDays,
      reminder_time: reminderTime,
      is_private: false,
    });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? "請檢查欄位");
      return;
    }
    try {
      await createMut.mutateAsync(parsed.data as AnniversaryCreateParsed);
      // W39.76 — cleared only once the anniversary exists server-side; clearing
      // on unmount is the thing that would make the draft pointless.
      formDraft.clear();
      pushToast({ variant: "success", message: "紀念日已建立" });
      nav("/anniversaries");
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
      } else {
        setFormError("建立失敗, 請稍後再試");
      }
    }
  };

  return (
    <div data-testid="new-anniversary-page" className="flex flex-col pb-40">
      <PageHero
        // W52.0 D-9 — a trailing `ChevronLeft` becomes the trail's first
        // segment. Nine pages carried a hand-written arrow in four different
        // shapes; `Breadcrumbs` is the one shape, and it also says 「你在哪一層」.
        crumbs={[
          { to: "/anniversaries", label: "紀念日", testId: "anniversary-new-back" },
          { label: "新增" },
        ]}
        eyebrow="新增"
        title="新增紀念日"
        subtitle="把重要的日子記下來"
      />

      <FormErrorBanner message={formError} />

      <PageContainer width="default" vertical="loose">
        <SectionStack density="default">
        <div className="flex justify-end">
          <AISuggestChip
            title={title}
            description={description}
            currentCategory={category}
            onAccept={(r) => {
              setCategory(r.category);
              if (r.category === "custom") {
                setCustomLabel(r.category);
              }
            }}
            onError={(msg) =>
              pushToast({ variant: "default", message: msg })
            }
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="ann-title" className="font-label text-ink-2">
            標題
          </label>
          <input
            id="ann-title"
            type="text"
            maxLength={100}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              formDraft.save({ title: e.target.value, description, customLabel });
            }}
            placeholder="例: 我們的交往紀念日"
            data-testid="ann-title"
            className="h-12 rounded-md border border-hairline bg-paper px-3 text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="ann-date" className="font-label text-ink-2">
            日期
          </label>
          <input
            id="ann-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            data-testid="ann-date"
            className="h-12 rounded-md border border-hairline bg-paper px-3 text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="font-label text-ink-2">分類</span>
          <CategoryPicker
            value={category}
            customLabel={customLabel}
            onCategoryChange={setCategory}
            onCustomLabelChange={setCustomLabel}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="ann-description" className="font-label text-ink-2">
            描述 (可選)
          </label>
          <textarea
            id="ann-description"
            // W46.0 — 2000, not 500. The server and the regenerated contract both
            // say 2000; this input still said 500, so a user writing a 600-char
            // description was silently blocked by the client while the column
            // had room for it. `input-constraint-contract.test.ts` exists to stop
            // exactly this drift, and it flagged the *contract* first — which is
            // how the committed `openapi.yaml` turned out to be the stale side.
            maxLength={2000}
            rows={4}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              formDraft.save({ title, description: e.target.value, customLabel });
            }}
            placeholder="例: 第一次說喜歡對方的那天"
            data-testid="ann-description"
            className="rounded-md border border-hairline bg-paper px-3 py-2 text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2"
          />
          <p className="text-meta text-ink-3 self-end tabular-nums">
            {description.length} / 500
          </p>
        </div>

        {/* R0 (W39.0): the audit measured the 16×16 checkbox, but the
            tap target is the whole `<label>` — and that label was 20px tall.
            `min-h-11` makes the label the 44pt target, which is the HIG-correct
            shape for a checkbox row: the box stays 16px (native size) while
            the tappable area is the row. */}
        <label className="flex min-h-11 w-full items-center gap-3">
          <input
            type="checkbox"
            checked={isRecurring}
            onChange={(e) => setIsRecurring(e.target.checked)}
            data-testid="ann-recurring"
            className="h-4 w-4 rounded border-hairline text-you-strong"
          />
          <span className="text-body-sm text-ink-1">每年重複</span>
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="font-label text-ink-2">提醒天數</span>
          <ReminderDaysPicker
            value={reminderDays}
            reminderTime={reminderTime}
            onChange={setReminderDays}
            onTimeChange={setReminderTime}
          />
        </div>
        </SectionStack>
      </PageContainer>

      <PageBottomActionBar
        primary={{
          label: createMut.isPending ? "儲存中…" : "儲存",
          onClick: () => void submit(),
          loading: createMut.isPending,
          testId: "anniversary-save",
        }}
        // W35.6: leave-the-page is a first-class affordance, not a generic
        // secondary action — migrated from `secondary` to `back` so every
        // create / edit page composes the same row.
        back={{
          label: "取消",
          onClick: () => nav("/anniversaries"),
          testId: "anniversary-cancel",
        }}
        testId="anniversary-bottom-bar"
      />
    </div>
  );
}